import { useCallback, useEffect, useState } from "react";
import moment from "moment-hijri";
import { useTranslation } from "react-i18next";
import { FaPlus } from "react-icons/fa";
import { Row, Col } from "react-bootstrap";
import LeftArrowImg from "../../../assets/img/icon/left_arrow.png";
import RightArrowImg from "../../../assets/img/icon/right_arrow.png";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import styles from "../css/calendar.module.css";
import API_URLS from "../../../constants/apiConstants";
import useAuth from "../../../hooks/useAuth";
import { EventModal } from "../../../components/components/js/EventModal";

export const Calendar = () => {
  const axiosPrivate = useAxiosPrivate();
  const { school } = useAuth();
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const lang = i18n.language;

  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(moment());
  const [slideDirection, setSlideDirection] = useState("next");
  const [showPicker, setShowPicker] = useState(false);
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const selectedDateKey = selectedDate.format("YYYY-MM-DD");
  const selectedDateEvents = events[selectedDateKey] || [];

  // Month days
  const startOfMonth = currentDate.clone().startOf("month").startOf("week");
  const endOfMonth = currentDate.clone().endOf("month").endOf("week");

  const monthDays = [];
  let dayCursor = startOfMonth.clone();
  while (dayCursor.isSameOrBefore(endOfMonth, "day")) {
    monthDays.push(dayCursor.clone());
    dayCursor.add(1, "day");
  }

  const hijriMonths = t("admin_pages.dashboard.calendar.hijri_months", {
    returnObjects: true,
  });
  const gregorianMonths = t("admin_pages.dashboard.calendar.gregorian_months", {
    returnObjects: true,
  });
  const weekDayNames = t("admin_pages.dashboard.calendar.weekdays", {
    returnObjects: true,
  });

  const handlePrevMonth = () => {
    setSlideDirection("prev");
    const newDate = currentDate.clone().subtract(1, "month");
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    setSlideDirection("next");
    const newDate = currentDate.clone().add(1, "month");
    setCurrentDate(newDate);
  };

  const toLocalizedNumber = (number) => {
    if (lang === "ar") {
      return number.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);
    }
    return number.toString();
  };

  // Get the localized month name based on current language
  const getLocalizedMonth = (date) => {
    if (lang === "ar") {
      return gregorianMonths[date.month()]; // Use the translated month names
    }
    return date.format("MMMM");
  };

  const onDateChange = (date) => {
    setSelectedDate(date);

    // Navigate to the previous or next month if the date is outside the current month
    if (!date.isSame(currentDate, "month")) {
      if (date.isBefore(currentDate, "month")) {
        handlePrevMonth();
      } else {
        handleNextMonth();
      }
    }
  };

  const monthYearPicker = () => {
    return (
      <div className={styles.monthYearPicker}>
        {/* Year header */}
        <div className={styles.pickerHeader}>
          <span
            className={styles.pickerArrow}
            onClick={() =>
              setCurrentDate((prev) => prev.clone().subtract(1, "year"))
            }
          >
            ‹
          </span>

          <span className={styles.pickerYear}>
            {toLocalizedNumber(currentDate.year())}
          </span>

          <span
            className={styles.pickerArrow}
            onClick={() =>
              setCurrentDate((prev) => prev.clone().add(1, "year"))
            }
          >
            ›
          </span>
        </div>

        {/* Months grid */}
        <div className={styles.monthGrid}>
          {gregorianMonths.map((month, index) => (
            <div
              key={index}
              className={`${styles.monthCell} ${
                index === currentDate.month() ? styles.activeMonth : ""
              }`}
              onClick={() => {
                setCurrentDate((prev) => prev.clone().month(index));
                setShowPicker(false);
              }}
            >
              {month}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const fetchMonthEvents = useCallback(() => {
    const startOfMonth = currentDate.clone().startOf("month").startOf("week");
    const endOfMonth = currentDate.clone().endOf("month").endOf("week");

    const start = startOfMonth.format("YYYY-MM-DD");
    const end = endOfMonth.format("YYYY-MM-DD");
    const url = API_URLS.rangeOfEvents();

    axiosPrivate
      .get(url, {
        params: { start_date: start, end_date: end, nursery_id: school.id },
      })
      .then((res) => {
        setEvents(res.data);
      })
      .catch((err) => console.error(err));
  }, [currentDate, school.id, axiosPrivate]);

  useEffect(() => {
    fetchMonthEvents();
  }, [fetchMonthEvents]);

  const handleAddClicked = () => {
    setShowEventModal(true);
    setSelectedEvent(null);
  };

  const handleEventClicked = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  return (
    <Row className="mt-4 unselectable">
      <Col sm={12} lg={9} className="mb-4">
        <div className="d-flex justify-content-center mb-3">
          <div className="d-flex align-items-center justify-content-between gap-5">
            <img
              src={dir === "rtl" ? RightArrowImg : LeftArrowImg}
              alt="left arrow"
              className={styles.arrowIcon}
              onClick={handlePrevMonth}
            />
            <div className={styles.calendarHeaderWrapper}>
              <span
                className={styles.calendarHeader}
                onClick={() => setShowPicker((prev) => !prev)}
              >
                {getLocalizedMonth(currentDate)}{" "}
                {toLocalizedNumber(currentDate.year())}
              </span>
              {showPicker && monthYearPicker()}
            </div>
            <img
              src={dir === "rtl" ? LeftArrowImg : RightArrowImg}
              alt="right arrow"
              className={styles.arrowIcon}
              onClick={handleNextMonth}
            />
          </div>
        </div>

        {/* Weekday header */}
        <div className={styles.weekHeader}>
          {weekDayNames.map((dayName, index) => (
            <div key={index} className={styles.weekHeaderCell}>
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div
          key={currentDate.format("YYYY-MM")} // forces re-animation
          className={`${styles.monthView} ${
            slideDirection === "next" ? styles.slideNext : styles.slidePrev
          }`}
        >
          {monthDays.map((day) => {
            const hijriDay = day.iDate();
            const hijriMonth = day.iMonth();
            const hijriYear = day.iYear();
            const gregorianDay = day.date();

            const isSelected = selectedDate?.isSame(day, "day");
            const isCurrentMonth = day.month() === currentDate.month();

            // Get events for this day
            const dayKey = day.format("YYYY-MM-DD");
            const dayEvents = events[dayKey] || [];

            return (
              <div
                key={day.toString()}
                className={`${styles.calendarTile} ${
                  isSelected ? styles.activeTile : ""
                } ${!isCurrentMonth ? styles.outsideMonth : ""}`}
                onClick={() => onDateChange(day)}
              >
                <div className={styles.dateDiv}>
                  <div className={styles.dayNum}>
                    {toLocalizedNumber(gregorianDay)}
                  </div>
                  <div
                    className={styles.hijriDayMonth}
                    title={`${hijriDay} ${hijriMonths[hijriMonth]} ${hijriYear}`}
                  >
                    <span>{toLocalizedNumber(hijriDay)}</span>
                    <span> / </span>
                    <span>{toLocalizedNumber(hijriMonth + 1)}</span>
                  </div>
                </div>
                <div
                  style={{ maxWidth: "100%", minWidth: 0 }}
                  className="d-flex flex-column gap-1 mt-auto"
                >
                  {dayEvents.slice(0, 1).map((event) => (
                    <p key={event.id} className={styles.eventName}>
                      {event.title}
                    </p>
                  ))}

                  {dayEvents.length > 1 && (
                    <p className={styles.moreEvents}>
                      +{dayEvents.length - 1} {t("admin_pages.calendar.more")}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Col>

      <Col sm={12} lg={3}>
        <div className={styles.eventsCol}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className={styles.eventsHeader}>
              {t("admin_pages.calendar.events")}
            </span>
            <div className={styles.addEventBtn} title={t("admin_pages.calendar.add")} onClick={handleAddClicked}>
              <FaPlus size={15} />
            </div>
          </div>
          <div className={styles.eventCardsList}>
            {selectedDateEvents.length ? (
              selectedDateEvents.map((event) => (
                <p key={event.id} className={styles.eventCard} onClick={() => handleEventClicked(event)}>
                  {event.title}
                </p>
              ))
            ) : (
              <div
                className={`d-flex flex-column justify-content-center align-items-center ${styles.h12rem}`}
              >
                <p className="my-5 small text-center text-secondary">
                  {t("admin_pages.dashboard.no_events")}
                </p>
              </div>
            )}
          </div>
        </div>
      </Col>
      
      <EventModal
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        event={selectedEvent} // if null --> add mode
        nurseryId={school.id}
        onSaved={() => fetchMonthEvents()}
        selectedDate={selectedDate}
      />
    </Row>
  );
};
